import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';

export type EventqInfoCardTone = 'neutral' | 'info' | 'success' | 'danger';

export interface EventqInfoCardMeta {
  label: string;
  /**
   * A string renders as one row. An array renders one row per entry, and every meta
   * column shares the same rows, so paired columns (days beside their hours) stay
   * aligned even when a row wraps onto a second line.
   */
  value: string | readonly string[];
}

export interface EventqInfoCardNote {
  label: string;
  text: string;
}

@Component({
  selector: 'eventq-info-card',
  templateUrl: './info-card.html',
  styleUrl: './info-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.eventq-info-card-host--neutral]': "tone() === 'neutral'",
    '[class.eventq-info-card-host--info]': "tone() === 'info'",
    '[class.eventq-info-card-host--success]': "tone() === 'success'",
    '[class.eventq-info-card-host--danger]': "tone() === 'danger'",
    '[attr.aria-busy]': 'loading() ? "true" : null',
  },
})
export class EventqInfoCard {
  readonly tone = input<EventqInfoCardTone>('neutral');
  readonly category = input('');
  readonly title = input.required<string>();
  readonly meta = input<readonly EventqInfoCardMeta[]>([]);
  /** Preferred multi-note API. When non-empty, takes precedence over `note`. */
  readonly notes = input<readonly EventqInfoCardNote[]>([]);
  /** Legacy single-note API — used when `notes` is empty. */
  readonly note = input<EventqInfoCardNote | null>(null);
  readonly loading = input(false, { transform: booleanAttribute });

  metaRows(item: EventqInfoCardMeta): readonly string[] {
    return Array.isArray(item.value) ? item.value : [item.value as string];
  }

  /** Only list values take part in the shared rows; a string is one free-flowing block. */
  isRowList(item: EventqInfoCardMeta): boolean {
    return Array.isArray(item.value);
  }

  /** Row track for the meta grid: one for the labels, then one per list row. */
  readonly metaRowTemplate = computed(() => {
    const rows = this.meta().reduce(
      (most, item) => (this.isRowList(item) ? Math.max(most, this.metaRows(item).length) : most),
      0,
    );
    return Array.from({ length: rows + 1 }, () => 'auto').join(' ');
  });

  readonly resolvedNotes = computed((): readonly EventqInfoCardNote[] => {
    const notes = this.notes();
    if (notes.length > 0) {
      return notes;
    }
    const legacy = this.note();
    return legacy ? [legacy] : [];
  });
}
